import { collectRecordKeys } from '~/common/subRegions';
import type { DataRequestV2 } from '~/common/types';
import type { UtilityAgency } from '../common';

type RequestScope = Pick<DataRequestV2, 'agencySubGeometryId'> & { agency: { agencyId: string } };
type RowScope = Pick<UtilityAgency, 'agencyId' | 'agencySubGeometryId' | 'isSubGeometryParent'>;

/** CG-611: a summary parent row — an agency split across more than one record. */
export function isSubGeometryParentRow(row: Pick<UtilityAgency, 'isSubGeometryParent'>): boolean {
  return row.isSubGeometryParent === true;
}

/**
 * CG-611: does a data request belong to a given table row, accounting for sub-geometry scoping?
 *
 * Deliberately data-driven, not flag-gated — the routing flag only gates request
 * *generation*; rows built from existing split requests must keep matching them.
 *
 * - A summary parent row owns no requests (its data lives on the Region children).
 * - A flat / non-split row (no sub-geometry, not a parent) matches all of its agency's requests.
 * - A Region child matches only requests in its own record: the catch-all child (sub-geometry
 *   `null`) matches null-scoped requests; a sub-geometry child matches its exact id.
 */
export function requestMatchesRow(request: RequestScope, row: RowScope): boolean {
  if (request.agency.agencyId !== row.agencyId) return false;
  if (row.isSubGeometryParent) return false;
  if (row.agencySubGeometryId === undefined) return true;
  return (request.agencySubGeometryId ?? null) === row.agencySubGeometryId;
}

/**
 * CG-611: build the Utilities table row model. An agency whose data requests span more
 * than one record (each distinct sub-geometry plus the catch-all/leftover bucket counts
 * as one record) becomes an expandable summary parent: the parent row carries only the
 * agency name, and every record is listed as a `[Agency] Region [N]` child, numbered by
 * the shared `collectRecordKeys` rule (first-appearance order, catch-all last) so the
 * numbering matches the admin request-details page. An agency with a single record stays
 * flat — so with request generation disabled this is a natural no-op, while agencies that
 * already have split requests keep showing them (display is data-driven, never flag-gated).
 */
export function buildAgencyRows(agencies: UtilityAgency[], dataRequests: DataRequestV2[]): UtilityAgency[] {
  return agencies.map((agency) => {
    const recordKeys = collectRecordKeys(dataRequests.filter((r) => r.agency.agencyId === agency.agencyId));

    if (recordKeys.length <= 1) return agency;

    const subRows: UtilityAgency[] = recordKeys.map((key, index) => ({
      ...agency,
      agencyName: `${agency.agencyName} Region ${index + 1}`,
      agencySubGeometryId: key,
      isSubGeometryParent: false,
      subRows: undefined,
    }));

    return { ...agency, subRows, isSubGeometryParent: true };
  });
}

/**
 * Sets `hasFiles` on every row and Region child: true when one of the row's own requests has a file, or for a
 * summary parent (which owns no requests) when any of its Regions has one. Worked out once per build of the
 * Utilities table's rows, so sorting the Files column doesn't rescan every request per comparison.
 */
export function withFileFlags(rows: UtilityAgency[], dataRequests: DataRequestV2[]): UtilityAgency[] {
  const mark = (row: UtilityAgency): UtilityAgency => {
    const subRows = row.subRows?.map(mark);
    const ownFiles = dataRequests.some((r) => requestMatchesRow(r, row) && r.files.length > 0);
    return { ...row, hasFiles: ownFiles || !!subRows?.some((c) => c.hasFiles), subRows };
  };
  return rows.map(mark);
}

/**
 * A row id that is unique across the tree. Region children copy their parent agency's `id`, so they add their
 * record key: the sub-geometry id, or `other` for the catch-all record (`agencySubGeometryId` null). Flat rows
 * and summary parents have no record key and keep the agency id.
 */
export function getUtilityRowId(row: Pick<UtilityAgency, 'id' | 'agencySubGeometryId'>): string {
  if (row.agencySubGeometryId === undefined) return String(row.id);
  return `${row.id}-region-${row.agencySubGeometryId ?? 'other'}`;
}
